import {
  DiscountHelperPricingData,
  DiscountHelperSource,
  DiscountHelperSourceReference,
} from '../types/discountHelper.types';

const COUPON = 'Coupon';

/**
 * Given an array of discount sources. Ensure that no discount source with the same promotion ID appears more than one time.
 * If more than one discount source have the same promotion ID, the first _active_ discount source is the only one that should remain in the list.
 * If none are active, the first one found should be the only remaining in the list.
 *
 * @param discountSources the discount sources to filter
 * @returns the filtered discount sources
 */
const ensureUniqueActiveDiscountSources = <
  DiscountSource extends DiscountHelperSource,
>(
  discountSources?: DiscountSource[]
) => {
  // to keep track of promotion IDs that have already been seen
  const seenPromotionIds = new Set<string>();

  return (
    discountSources?.reduce<DiscountSource[]>(
      (filtered, currentDiscountSource) => {
        // if we have not already processed a discount source with this promotion ID
        if (!seenPromotionIds.has(currentDiscountSource.promotionId)) {
          // mark it as processed
          seenPromotionIds.add(currentDiscountSource.promotionId);
          // and append it to the filtered array
          return [...filtered, currentDiscountSource];
        }
        // otherwise
        if (
          // if the current discount source is active
          currentDiscountSource.active &&
          // and the discount source currently in the list is not active
          !!filtered.find(
            ds =>
              ds.promotionId === currentDiscountSource.promotionId && !ds.active
          )
        ) {
          // remove the old, inactive discount source and append the new, active discount source
          return [
            ...filtered.filter(
              ds => ds.promotionId !== currentDiscountSource.promotionId
            ),
            currentDiscountSource,
          ];
        }
        // otherwise, do nothing
        return filtered;
      },
      []
    ) ?? []
  );
};

/**
 * A helper method to categorize a set of discount sources into correct item and shipping discount sources. Given pricing data
 * for each line item and pricing data for shipping, categorize the provided discount sources into either item
 * or shipping discount sources.
 *
 * Discount sources that cannot be displayed (not a coupon and not applying any discounts) will not be included in the response.
 *
 * Discount sources that should be displayed (either a coupon or applying a discount) that cannot be confidently attributed to
 * items or shipping will be included as an item discount source.
 *
 * The `DiscountSource` type is generic because this helper should not be the definitive source of what a `DiscountSource` type looks like.
 * The helper specifies that the `DiscountSource` objects passed must include the properties necessary for it to function. By typing the
 * argument generically consumer that passes a typed object with more than the necessary properties will not have those properties erased
 * off of the type by this function.
 *
 * @param pricingData pricing data with discount sources for items, keyed on the line item ID
 * @param shippingPricingData pricing data with discount sources for shipping data
 * @param discountSources the total list of discount sources to categorize
 * @returns lists of item discount sources and shipping discount sources as well as the individual discount source to display for each (if available)
 */
export const discountHelper = <DiscountSource extends DiscountHelperSource>(
  pricingData?: Record<string, DiscountHelperPricingData>,
  shippingPricingData?: DiscountHelperPricingData,
  discountSources?: DiscountSource[]
): {
  itemDiscount: DiscountSource | undefined;
  shippingDiscount: DiscountSource | undefined;
  itemDiscounts: DiscountSource[];
  shippingDiscounts: DiscountSource[];
} => {
  const uniqueDiscountSources =
    ensureUniqueActiveDiscountSources(discountSources);

  // promotion IDs affecting shipping
  const shippingPromotionIds =
    shippingPricingData?.discountSources?.map(
      (ds: DiscountHelperSourceReference) => ds.promotionId
    ) ?? [];

  // promotion IDs affecting items
  const itemPromotionIds = Object.values(pricingData ?? {}).flatMap(
    elementPricingData =>
      elementPricingData?.discountSources
        .filter(
          ds =>
            uniqueDiscountSources.find(
              oDs => oDs.promotionId === ds.promotionId
            )?.active
        )
        .map(ds => ds.promotionId)
  );

  // all the full discount source objects that correspond to a shipping discount
  const shippingDiscounts = uniqueDiscountSources.filter(discount =>
    shippingPromotionIds.includes(discount.promotionId)
  );
  // all the full discount source objects that correspond to an item discount
  const itemDiscounts = uniqueDiscountSources.filter(discount =>
    itemPromotionIds.includes(discount.promotionId)
  );
  // all the full discount source objects that do not correspond to either an item or shipping discount
  // filtered to the discount sources we can display (must be either active or a coupon)
  const displayableUnassignedDiscountSources = uniqueDiscountSources.filter(
    discount =>
      !itemPromotionIds.includes(discount.promotionId) &&
      !shippingPromotionIds.includes(discount.promotionId) &&
      // unassignable discount sources are only displayable if
      // 1) active=true means it is still discounting _something_ on the cart (this case is typically hit when evaluating referenced carts for discounts to display in template price summary)
      // 2) it is a coupon type (the customer explicitly placed this on the cart, even if it applied no discount)
      (discount.active || discount.type === COUPON)
  );

  const displayableItemDiscountSources = [
    ...itemDiscounts,
    ...displayableUnassignedDiscountSources,
  ];

  return {
    // item discount is the first one available (if it exists)
    itemDiscount: displayableItemDiscountSources[0],
    // shipping discount should be the first one available (if it exists)
    shippingDiscount: shippingDiscounts[0],
    // item discounts should include all the discount sources we can display
    itemDiscounts: displayableItemDiscountSources,
    shippingDiscounts,
  };
};
